import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Copy,
  Edit2,
  Eye,
  EyeOff,
  HelpCircle,
  Link2,
  Loader2,
  Lock,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MobileLayout } from "../components/layout/MobileLayout.jsx";
import { Button } from "../components/ui/Button.jsx";
import {
  Card,
  CardContent,
} from "../components/ui/Card.jsx";
import { Input } from "../components/ui/Input.jsx";
import { Label } from "../components/ui/Label.jsx";
import { useToast } from "../hooks/useToast.js";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/Dialog.jsx";
import {
  createField,
  createOption,
  deleteField,
  deleteOption,
  fetchFields,
  updateField,
  updateOption,
} from "../services/dynamicFieldService.js";
import { getWebhookInfo, regenerateApiKey } from "../services/companyService.js";
import {
  fetchStages,
  createStage,
  updateStage,
  deleteStage,
  toggleStageDashboard,
} from "../services/leadStageService.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/Select.jsx";

export default function Settings() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedField, setExpandedField] = useState(null);

  // Add field state
  const [showAddField, setShowAddField] = useState(false);
  const [newFieldLabel, setNewFieldLabel] = useState("");
  const [addingField, setAddingField] = useState(false);

  // Edit field state
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [editFieldLabel, setEditFieldLabel] = useState("");
  const [savingField, setSavingField] = useState(false);

  // Add option state
  const [addingOptionForField, setAddingOptionForField] = useState(null);
  const [newOptionLabel, setNewOptionLabel] = useState("");
  const [savingOption, setSavingOption] = useState(false);

  // Edit option state
  const [editingOptionId, setEditingOptionId] = useState(null);
  const [editOptionLabel, setEditOptionLabel] = useState("");
  const [savingEditOption, setSavingEditOption] = useState(false);

  // Lead stages state
  const [stages, setStages] = useState([]);
  const [showAddStage, setShowAddStage] = useState(false);
  const [newStageLabel, setNewStageLabel] = useState("");
  const [newStageType, setNewStageType] = useState("in_progress");
  const [addingStage, setAddingStage] = useState(false);
  const [editingStageId, setEditingStageId] = useState(null);
  const [editStageLabel, setEditStageLabel] = useState("");
  const [editStageType, setEditStageType] = useState("in_progress");
  const [savingStage, setSavingStage] = useState(false);

  // Webhook state
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [showWebhookGuide, setShowWebhookGuide] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  // ─── Load webhook info ────────────────────────────────

  useEffect(() => {
    (async () => {
      try {
        const res = await getWebhookInfo();
        setWebhookInfo(res.data);
      } catch {}
    })();
  }, []);

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast({ title: `${label} copied to clipboard` });
  };

  const handleRegenerate = async () => {
    if (!window.confirm("Regenerate API key? The old key will stop working immediately.")) return;
    setRegenerating(true);
    try {
      const res = await regenerateApiKey();
      setWebhookInfo(res.data);
      setShowApiKey(true);
      toast({ title: "API key regenerated" });
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: err.message });
    } finally {
      setRegenerating(false);
    }
  };

  // ─── Load stages ─────────────────────────────────────

  const loadStages = async () => {
    try {
      const res = await fetchStages();
      setStages(res.data || []);
    } catch {}
  };

  useEffect(() => { loadStages(); }, []);

  const handleAddStage = async () => {
    if (!newStageLabel.trim()) return;
    setAddingStage(true);
    try {
      await createStage({ stage_label: newStageLabel.trim(), stage_type: newStageType });
      toast({ title: "Stage created" });
      setNewStageLabel("");
      setNewStageType("in_progress");
      setShowAddStage(false);
      await loadStages();
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: err.message });
    } finally {
      setAddingStage(false);
    }
  };

  const handleUpdateStage = async (stageId) => {
    if (!editStageLabel.trim()) return;
    setSavingStage(true);
    try {
      const stage = stages.find((s) => s.id === stageId);
      await updateStage(stageId, {
        stage_label: editStageLabel.trim(),
        stage_type: editStageType,
        sort_order: stage?.sort_order || 0,
      });
      toast({ title: "Stage updated" });
      setEditingStageId(null);
      await loadStages();
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: err.message });
    } finally {
      setSavingStage(false);
    }
  };

  const handleDeleteStage = async (stageId, label) => {
    if (!window.confirm(`Delete stage "${label}"?`)) return;
    try {
      await deleteStage(stageId);
      toast({ title: "Stage deleted" });
      await loadStages();
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: err.message });
    }
  };

  const stageTypeColors = {
    new: "bg-blue-50 text-blue-600",
    in_progress: "bg-yellow-50 text-yellow-700",
    won: "bg-green-50 text-green-600",
    lost: "bg-red-50 text-red-600",
  };

  // ─── Load fields ──────────────────────────────────────

  const loadFields = async () => {
    try {
      setLoading(true);
      const res = await fetchFields();
      setFields(res.data || []);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: err.message || "Failed to load fields",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFields();
  }, []);

  // ─── Field CRUD ───────────────────────────────────────

  const handleAddField = async () => {
    if (!newFieldLabel.trim()) return;
    setAddingField(true);
    try {
      await createField({ field_label: newFieldLabel.trim() });
      toast({ title: "Field created successfully" });
      setNewFieldLabel("");
      setShowAddField(false);
      await loadFields();
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: err.message,
      });
    } finally {
      setAddingField(false);
    }
  };

  const handleUpdateField = async (fieldId) => {
    if (!editFieldLabel.trim()) return;
    setSavingField(true);
    try {
      const field = fields.find((f) => f.id === fieldId);
      await updateField(fieldId, {
        field_label: editFieldLabel.trim(),
        field_type: field?.field_type || "dropdown",
        sort_order: field?.sort_order || 0,
      });
      toast({ title: "Field updated successfully" });
      setEditingFieldId(null);
      setEditFieldLabel("");
      await loadFields();
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: err.message,
      });
    } finally {
      setSavingField(false);
    }
  };

  const handleDeleteField = async (fieldId, fieldLabel) => {
    if (!window.confirm(`Delete field "${fieldLabel}" and all its options?`))
      return;
    try {
      await deleteField(fieldId);
      toast({ title: "Field deleted" });
      if (expandedField === fieldId) setExpandedField(null);
      await loadFields();
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: err.message,
      });
    }
  };

  // ─── Option CRUD ──────────────────────────────────────

  const handleAddOption = async (fieldId) => {
    if (!newOptionLabel.trim()) return;
    setSavingOption(true);
    try {
      await createOption(fieldId, { option_label: newOptionLabel.trim() });
      toast({ title: "Option added" });
      setNewOptionLabel("");
      setAddingOptionForField(null);
      await loadFields();
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: err.message,
      });
    } finally {
      setSavingOption(false);
    }
  };

  const handleUpdateOption = async (optionId) => {
    if (!editOptionLabel.trim()) return;
    setSavingEditOption(true);
    try {
      await updateOption(optionId, { option_label: editOptionLabel.trim() });
      toast({ title: "Option updated" });
      setEditingOptionId(null);
      setEditOptionLabel("");
      await loadFields();
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: err.message,
      });
    } finally {
      setSavingEditOption(false);
    }
  };

  const handleDeleteOption = async (optionId, optionLabel) => {
    if (!window.confirm(`Delete option "${optionLabel}"?`)) return;
    try {
      await deleteOption(optionId);
      toast({ title: "Option deleted" });
      await loadFields();
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: err.message,
      });
    }
  };

  // ─── Render ───────────────────────────────────────────

  return (
    <MobileLayout showNavigation={true}>
      <div className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-full"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-bold">Settings</h1>
        </div>

        {/* ─── Webhook Integration ─────────────────────────── */}
        {webhookInfo && (
          <Card className="border-2 shadow-sm border-blue-100/50 rounded-xl">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Link2 size={16} className="text-[#00a8b4]" />
                  <h2 className="font-bold text-sm">Webhook Integration</h2>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-[10px] text-[#00a8b4] gap-1 px-2"
                  onClick={() => setShowWebhookGuide(true)}
                >
                  <HelpCircle size={12} />
                  How it works
                </Button>
              </div>
              <p className="text-[11px] text-gray-500">
                Use this URL to receive leads from external sources (website forms, Zapier, Google Ads, etc.)
              </p>

              {/* Webhook URL */}
              <div>
                <Label className="text-[10px] text-gray-400 uppercase tracking-wide">Webhook URL</Label>
                <div className="flex gap-1 mt-1">
                  <Input
                    value={webhookInfo.webhook_url}
                    readOnly
                    className="h-7 text-[11px] font-mono bg-gray-50 flex-1"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={() => handleCopy(webhookInfo.webhook_url, "Webhook URL")}
                  >
                    <Copy size={12} />
                  </Button>
                </div>
              </div>

              {/* API Key */}
              <div>
                <Label className="text-[10px] text-gray-400 uppercase tracking-wide">API Key</Label>
                <div className="flex gap-1 mt-1">
                  <Input
                    value={showApiKey ? webhookInfo.api_key : "••••••••••••••••••••••••"}
                    readOnly
                    className="h-7 text-[11px] font-mono bg-gray-50 flex-1"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={() => setShowApiKey(!showApiKey)}
                  >
                    {showApiKey ? <EyeOff size={12} /> : <Eye size={12} />}
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={() => handleCopy(webhookInfo.api_key, "API Key")}
                  >
                    <Copy size={12} />
                  </Button>
                </div>
              </div>

              {/* Regenerate + Example */}
              <div className="flex items-center justify-between pt-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-[10px] text-gray-500 gap-1 px-2"
                  onClick={handleRegenerate}
                  disabled={regenerating}
                >
                  <RefreshCw size={10} />
                  {regenerating ? "Regenerating..." : "Regenerate Key"}
                </Button>
              </div>

              {/* Supported Fields */}
              <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Supported Fields</p>

                <div className="space-y-1">
                  <p className="text-[10px] font-semibold text-gray-700">Required</p>
                  <div className="flex flex-wrap gap-1">
                    <span className="text-[9px] px-2 py-0.5 rounded bg-red-50 text-red-600 font-mono">name *</span>
                    <span className="text-[9px] px-2 py-0.5 rounded bg-red-50 text-red-600 font-mono">phone_number *</span>
                  </div>
                </div>

                <div className="space-y-1">
                  <p className="text-[10px] font-semibold text-gray-700">Optional (auto-mapped to lead fields)</p>
                  <div className="flex flex-wrap gap-1">
                    {["email", "source", "service_name", "business_category", "industry_field", "address", "state", "city", "pin_code", "notes", "agent_email"].map((f) => (
                      <span key={f} className="text-[9px] px-2 py-0.5 rounded bg-white border border-gray-200 text-gray-600 font-mono">{f}</span>
                    ))}
                  </div>
                </div>

                <p className="text-[9px] text-gray-400">
                  <span className="font-medium text-gray-500">source</span> auto-matches your Lead Source options.
                  Any extra fields are saved in notes — nothing is lost.
                </p>
              </div>

              {/* JSON Example */}
              <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Example Payload</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 text-[9px] text-gray-400 gap-1 px-1"
                    onClick={() =>
                      handleCopy(
                        JSON.stringify({
                          name: "John Doe",
                          phone_number: "9876543210",
                          email: "john@example.com",
                          source: "Google Ads",
                          service_name: "Web Development",
                          notes: "From landing page",
                        }, null, 2),
                        "JSON example"
                      )
                    }
                  >
                    <Copy size={8} /> Copy
                  </Button>
                </div>
                <pre className="text-[9px] text-gray-600 font-mono leading-relaxed">{`{
  "name": "John Doe",
  "phone_number": "9876543210",
  "email": "john@example.com",
  "source": "Google Ads",
  "service_name": "Web Development",
  "notes": "From landing page"
}`}</pre>
              </div>

              {/* cURL Example */}
              <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">cURL Example</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 text-[9px] text-gray-400 gap-1 px-1"
                    onClick={() =>
                      handleCopy(
                        `curl -X POST "${webhookInfo.webhook_url}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"name":"John Doe","phone_number":"9876543210","email":"john@example.com","source":"Google Ads"}'`,
                        "cURL example"
                      )
                    }
                  >
                    <Copy size={8} /> Copy
                  </Button>
                </div>
                <pre className="text-[9px] text-gray-600 whitespace-pre-wrap break-all font-mono leading-relaxed">
{`curl -X POST "${webhookInfo.webhook_url}" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"John Doe","phone_number":"9876543210","email":"john@example.com","source":"Google Ads"}'`}
                </pre>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ─── Lead Stages ──────────────────────────────────── */}
        <h2 className="font-bold text-sm pt-2">Lead Stages</h2>
        <p className="text-xs text-gray-500 px-1">
          Configure the deal pipeline stages for your leads.
        </p>

        {!showAddStage && (
          <Button
            onClick={() => setShowAddStage(true)}
            className="h-8 px-3 text-xs text-white rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391]"
          >
            <Plus size={14} className="mr-1" />
            Add Stage
          </Button>
        )}

        {showAddStage && (
          <Card className="border-2 border-blue-100/50">
            <CardContent className="p-4 space-y-2">
              <Label className="text-xs font-semibold">New Stage</Label>
              <div className="flex gap-2">
                <Input
                  value={newStageLabel}
                  onChange={(e) => setNewStageLabel(e.target.value)}
                  placeholder="e.g. Follow Up, Negotiation"
                  className="h-8 text-xs flex-1"
                  onKeyDown={(e) => e.key === "Enter" && handleAddStage()}
                  autoFocus
                />
                <Select value={newStageType} onValueChange={setNewStageType}>
                  <SelectTrigger className="h-8 text-xs w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="won">Won</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  onClick={handleAddStage}
                  disabled={addingStage || !newStageLabel.trim()}
                  className="h-8 px-3 text-xs text-white rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391]"
                >
                  {addingStage ? <Loader2 size={14} className="animate-spin" /> : "Save"}
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setShowAddStage(false); setNewStageLabel(""); }}>
                  <X size={14} />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {stages.map((stage) => (
          <Card key={stage.id} className="border-2 shadow-sm border-blue-100/50 rounded-xl">
            <CardContent className="p-0">
              <div className="flex items-center justify-between px-4 py-3">
                {/* System stages: view-only, no edit/delete */}
                {stage.is_system ? (
                  <>
                    <div className="flex items-center gap-2 flex-1">
                      <span className="text-sm font-semibold text-gray-800">{stage.stage_label}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${stageTypeColors[stage.stage_type] || "bg-gray-100 text-gray-600"}`}>
                        {stage.stage_type}
                      </span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 flex items-center gap-0.5">
                        <Lock size={8} /> Locked
                      </span>
                      {stage.show_on_dashboard ? (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-500">Dashboard</span>
                      ) : null}
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          const res = await toggleStageDashboard(stage.id);
                          toast({ title: res.message });
                          await loadStages();
                        } catch (err) {
                          toast({ variant: "destructive", title: "Error", description: err.message });
                        }
                      }}
                      className={`text-[9px] px-1.5 py-0.5 rounded border ${
                        stage.show_on_dashboard
                          ? "border-blue-200 text-blue-500 bg-blue-50"
                          : "border-gray-200 text-gray-400"
                      }`}
                      title={stage.show_on_dashboard ? "Hide from dashboard" : "Show on dashboard"}
                    >
                      {stage.show_on_dashboard ? "On" : "Off"}
                    </button>
                  </>
                ) : editingStageId === stage.id ? (
                  /* Custom stages: editable */
                  <div className="flex gap-2 flex-1 mr-2">
                    <Input
                      value={editStageLabel}
                      onChange={(e) => setEditStageLabel(e.target.value)}
                      className="h-7 text-xs flex-1"
                      onKeyDown={(e) => e.key === "Enter" && handleUpdateStage(stage.id)}
                      autoFocus
                    />
                    <Select value={editStageType} onValueChange={setEditStageType}>
                      <SelectTrigger className="h-7 text-xs w-28">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="won">Won</SelectItem>
                        <SelectItem value="lost">Lost</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={() => handleUpdateStage(stage.id)} disabled={savingStage} className="h-7 px-2 text-xs text-white rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391]">Save</Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditingStageId(null)}><X size={12} /></Button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 flex-1">
                      <span className="text-sm font-semibold text-gray-800">{stage.stage_label}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${stageTypeColors[stage.stage_type] || "bg-gray-100 text-gray-600"}`}>
                        {stage.stage_type}
                      </span>
                      {stage.show_on_dashboard ? (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-500">Dashboard</span>
                      ) : null}
                    </div>
                    <div className="flex gap-1 items-center">
                      <button
                        onClick={async () => {
                          try {
                            const res = await toggleStageDashboard(stage.id);
                            toast({ title: res.message });
                            await loadStages();
                          } catch (err) {
                            toast({ variant: "destructive", title: "Error", description: err.message });
                          }
                        }}
                        className={`text-[9px] px-1.5 py-0.5 rounded border ${
                          stage.show_on_dashboard
                            ? "border-blue-200 text-blue-500 bg-blue-50"
                            : "border-gray-200 text-gray-400"
                        }`}
                        title={stage.show_on_dashboard ? "Hide from dashboard" : "Show on dashboard"}
                      >
                        {stage.show_on_dashboard ? "On" : "Off"}
                      </button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditingStageId(stage.id); setEditStageLabel(stage.stage_label); setEditStageType(stage.stage_type); }}>
                        <Edit2 size={13} className="text-gray-500" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDeleteStage(stage.id, stage.stage_label)}>
                        <Trash2 size={13} className="text-red-400" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        {/* ─── Custom Fields ───────────────────────────────── */}
        <h2 className="font-bold text-sm pt-2">Custom Fields</h2>
        <p className="text-xs text-gray-500 px-1">
          Manage dropdown fields and their options used in lead forms. These
          fields appear as dropdowns when adding or editing leads.
        </p>

        {/* Add Field Button */}
        {!showAddField && (
          <Button
            onClick={() => setShowAddField(true)}
            className="h-8 px-3 text-xs text-white rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391]"
          >
            <Plus size={14} className="mr-1" />
            Add Field
          </Button>
        )}

        {/* Add Field Form */}
        {showAddField && (
          <Card className="border-2 border-blue-100/50">
            <CardContent className="p-4 space-y-3">
              <Label className="text-xs font-semibold">New Field Name</Label>
              <div className="flex gap-2">
                <Input
                  value={newFieldLabel}
                  onChange={(e) => setNewFieldLabel(e.target.value)}
                  placeholder="e.g. Service Name, Industry, Source"
                  className="h-8 text-xs flex-1"
                  onKeyDown={(e) => e.key === "Enter" && handleAddField()}
                  autoFocus
                />
                <Button
                  onClick={handleAddField}
                  disabled={addingField || !newFieldLabel.trim()}
                  className="h-8 px-3 text-xs text-white rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391]"
                >
                  {addingField ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    "Save"
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => {
                    setShowAddField(false);
                    setNewFieldLabel("");
                  }}
                >
                  <X size={14} />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        )}

        {/* Empty State */}
        {!loading && fields.length === 0 && (
          <div className="text-center py-10 text-gray-400 text-sm">
            No custom fields yet. Add one to get started.
          </div>
        )}

        {/* Fields List */}
        {!loading &&
          fields.map((field) => (
            <Card
              key={field.id}
              className="border-2 shadow-sm border-blue-100/50 rounded-xl overflow-hidden"
            >
              <CardContent className="p-0">
                {/* Field Header */}
                <div className="flex items-center justify-between px-4 py-3">
                  {editingFieldId === field.id ? (
                    <div className="flex gap-2 flex-1 mr-2">
                      <Input
                        value={editFieldLabel}
                        onChange={(e) => setEditFieldLabel(e.target.value)}
                        className="h-7 text-xs flex-1"
                        onKeyDown={(e) =>
                          e.key === "Enter" && handleUpdateField(field.id)
                        }
                        autoFocus
                      />
                      <Button
                        onClick={() => handleUpdateField(field.id)}
                        disabled={savingField || !editFieldLabel.trim()}
                        className="h-7 px-2 text-xs text-white rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391]"
                      >
                        {savingField ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          "Save"
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => {
                          setEditingFieldId(null);
                          setEditFieldLabel("");
                        }}
                      >
                        <X size={12} />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() =>
                          setExpandedField(
                            expandedField === field.id ? null : field.id
                          )
                        }
                        className="flex items-center gap-2 flex-1 text-left"
                      >
                        {expandedField === field.id ? (
                          <ChevronDown size={16} className="text-gray-400" />
                        ) : (
                          <ChevronRight size={16} className="text-gray-400" />
                        )}
                        <span className="text-sm font-semibold text-gray-800">
                          {field.field_label}
                        </span>
                        {field.is_system && (
                          <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                            <Lock size={8} /> Required
                          </span>
                        )}
                        <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                          {field.options?.length || 0} options
                        </span>
                      </button>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => {
                            setEditingFieldId(field.id);
                            setEditFieldLabel(field.field_label);
                          }}
                        >
                          <Edit2 size={13} className="text-gray-500" />
                        </Button>
                        {!field.is_system && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() =>
                              handleDeleteField(field.id, field.field_label)
                            }
                          >
                            <Trash2 size={13} className="text-red-400" />
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Expanded Options */}
                {expandedField === field.id && (
                  <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-3 space-y-2">
                    {/* Options list */}
                    {field.options?.length > 0 ? (
                      field.options.map((option) => (
                        <div
                          key={option.id}
                          className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-100"
                        >
                          {editingOptionId === option.id ? (
                            <div className="flex gap-2 flex-1">
                              <Input
                                value={editOptionLabel}
                                onChange={(e) =>
                                  setEditOptionLabel(e.target.value)
                                }
                                className="h-7 text-xs flex-1"
                                onKeyDown={(e) =>
                                  e.key === "Enter" &&
                                  handleUpdateOption(option.id)
                                }
                                autoFocus
                              />
                              <Button
                                onClick={() => handleUpdateOption(option.id)}
                                disabled={
                                  savingEditOption || !editOptionLabel.trim()
                                }
                                className="h-7 px-2 text-xs text-white rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391]"
                              >
                                {savingEditOption ? (
                                  <Loader2
                                    size={12}
                                    className="animate-spin"
                                  />
                                ) : (
                                  "Save"
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => {
                                  setEditingOptionId(null);
                                  setEditOptionLabel("");
                                }}
                              >
                                <X size={12} />
                              </Button>
                            </div>
                          ) : (
                            <>
                              <span className="text-xs text-gray-700">
                                {option.option_label}
                              </span>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() => {
                                    setEditingOptionId(option.id);
                                    setEditOptionLabel(option.option_label);
                                  }}
                                >
                                  <Edit2
                                    size={11}
                                    className="text-gray-400"
                                  />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() =>
                                    handleDeleteOption(
                                      option.id,
                                      option.option_label
                                    )
                                  }
                                >
                                  <Trash2
                                    size={11}
                                    className="text-red-400"
                                  />
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-gray-400 text-center py-2">
                        No options yet
                      </p>
                    )}

                    {/* Add option */}
                    {addingOptionForField === field.id ? (
                      <div className="flex gap-2 pt-1">
                        <Input
                          value={newOptionLabel}
                          onChange={(e) => setNewOptionLabel(e.target.value)}
                          placeholder="Option name"
                          className="h-7 text-xs flex-1"
                          onKeyDown={(e) =>
                            e.key === "Enter" && handleAddOption(field.id)
                          }
                          autoFocus
                        />
                        <Button
                          onClick={() => handleAddOption(field.id)}
                          disabled={savingOption || !newOptionLabel.trim()}
                          className="h-7 px-2 text-xs text-white rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391]"
                        >
                          {savingOption ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : (
                            "Add"
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => {
                            setAddingOptionForField(null);
                            setNewOptionLabel("");
                          }}
                        >
                          <X size={12} />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={() => {
                          setAddingOptionForField(field.id);
                          setNewOptionLabel("");
                        }}
                        className="h-7 px-3 text-xs rounded-full w-full border-dashed"
                      >
                        <Plus size={12} className="mr-1" />
                        Add Option
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
      </div>

      {/* Webhook Integration Guide Modal */}
      <Dialog open={showWebhookGuide} onOpenChange={setShowWebhookGuide}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Link2 size={18} className="text-[#00a8b4]" />
              Webhook Integration Guide
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 text-xs text-gray-700 leading-relaxed">
            {/* What is it */}
            <div>
              <h3 className="font-semibold text-sm text-gray-900 mb-1">What is a Webhook?</h3>
              <p className="text-gray-600">
                A webhook is a URL that receives lead data from external sources like your website forms,
                landing pages, Zapier, Google Ads, or any third-party tool. When someone fills out a form,
                the data is sent to your webhook URL and a new lead is automatically created in your CRM.
              </p>
            </div>

            {/* How to use */}
            <div>
              <h3 className="font-semibold text-sm text-gray-900 mb-1">How to Use</h3>
              <ol className="list-decimal pl-4 space-y-1 text-gray-600">
                <li>Copy your <span className="font-medium text-gray-800">Webhook URL</span> from above</li>
                <li>Set your website form or third-party tool to send a <span className="font-mono bg-gray-100 px-1 rounded">POST</span> request to this URL</li>
                <li>Send lead data as JSON in the request body</li>
                <li>The lead will appear in your CRM instantly</li>
              </ol>
            </div>

            {/* Required fields */}
            <div>
              <h3 className="font-semibold text-sm text-gray-900 mb-1">Required Fields</h3>
              <p className="text-gray-600 mb-2">Every request must include at minimum:</p>
              <div className="flex gap-2">
                <span className="font-mono text-[11px] px-2 py-1 rounded bg-red-50 text-red-600 border border-red-200">name</span>
                <span className="font-mono text-[11px] px-2 py-1 rounded bg-red-50 text-red-600 border border-red-200">phone_number</span>
              </div>
            </div>

            {/* Optional fields */}
            <div>
              <h3 className="font-semibold text-sm text-gray-900 mb-1">Optional Fields</h3>
              <p className="text-gray-600 mb-2">These fields are auto-mapped to your lead form:</p>
              <div className="flex flex-wrap gap-1.5">
                {["email", "source", "service_name", "business_category", "industry_field", "address", "state", "city", "pin_code", "notes", "agent_email"].map((f) => (
                  <span key={f} className="font-mono text-[10px] px-2 py-0.5 rounded bg-gray-50 border border-gray-200 text-gray-600">{f}</span>
                ))}
              </div>
            </div>

            {/* Field naming */}
            <div className="bg-blue-50 rounded-lg p-3">
              <h3 className="font-semibold text-sm text-blue-900 mb-1">Field Naming Convention</h3>
              <p className="text-blue-800 text-[11px]">
                If your CRM field name has multiple words, use <span className="font-mono font-semibold">underscore (_)</span> to separate them in the API body.
              </p>
              <div className="mt-2 space-y-1">
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="text-blue-600">Service Name</span>
                  <span className="text-blue-400">→</span>
                  <span className="font-mono bg-white px-1.5 py-0.5 rounded border border-blue-200 text-blue-700">service_name</span>
                </div>
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="text-blue-600">Business Category</span>
                  <span className="text-blue-400">→</span>
                  <span className="font-mono bg-white px-1.5 py-0.5 rounded border border-blue-200 text-blue-700">business_category</span>
                </div>
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="text-blue-600">Lead Source</span>
                  <span className="text-blue-400">→</span>
                  <span className="font-mono bg-white px-1.5 py-0.5 rounded border border-blue-200 text-blue-700">source</span>
                </div>
              </div>
            </div>

            {/* Smart matching */}
            <div className="bg-green-50 rounded-lg p-3">
              <h3 className="font-semibold text-sm text-green-900 mb-1">Smart Field Matching</h3>
              <p className="text-green-800 text-[11px] mb-2">
                When you send data, the system automatically tries to match it with your configured dropdown options:
              </p>
              <div className="space-y-2 text-[11px]">
                <div className="flex items-start gap-2">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <div>
                    <span className="font-medium text-green-900">Match found</span>
                    <span className="text-green-700"> — If you send </span>
                    <span className="font-mono bg-white px-1 rounded">"source": "Google Ads"</span>
                    <span className="text-green-700"> and your Lead Source options include "Google Ads", it will be automatically selected in the lead.</span>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <div>
                    <span className="font-medium text-green-900">No match</span>
                    <span className="text-green-700"> — If the value doesn't match any option, it's saved in the lead's </span>
                    <span className="font-medium">notes</span>
                    <span className="text-green-700"> section. No data is ever lost.</span>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <div>
                    <span className="font-medium text-green-900">Case insensitive</span>
                    <span className="text-green-700"> — "google ads", "Google Ads", "GOOGLE ADS" all match the same option. Only spelling and spacing matter.</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Extra fields */}
            <div className="bg-amber-50 rounded-lg p-3">
              <h3 className="font-semibold text-sm text-amber-900 mb-1">Extra / Unknown Fields</h3>
              <p className="text-amber-800 text-[11px]">
                You can send <span className="font-medium">any additional fields</span> in the request body — they don't need to match your CRM fields.
                Extra fields like <span className="font-mono bg-white px-1 rounded">budget</span>,
                <span className="font-mono bg-white px-1 rounded">page_url</span>,
                <span className="font-mono bg-white px-1 rounded">utm_source</span> etc.
                will all be automatically saved in the lead's notes section.
                <span className="font-semibold"> Nothing is ever ignored or lost.</span>
              </p>
            </div>

            {/* Agent assignment */}
            <div>
              <h3 className="font-semibold text-sm text-gray-900 mb-1">Auto-assign to Team Member</h3>
              <p className="text-gray-600">
                Send <span className="font-mono bg-gray-100 px-1 rounded">agent_email</span> with a team member's email address
                to automatically assign the lead to them. If the email doesn't match any team member,
                the lead is created unassigned.
              </p>
            </div>

            {/* Example */}
            <div>
              <h3 className="font-semibold text-sm text-gray-900 mb-1">Full Example</h3>
              <pre className="bg-gray-900 text-green-400 text-[10px] p-3 rounded-lg overflow-x-auto font-mono leading-relaxed">{`POST ${webhookInfo?.webhook_url || "https://api.crm.nipralo.com/v1/in/your_api_key"}

Headers:
  Content-Type: application/json

Body:
{
  "name": "John Doe",
  "phone_number": "9876543210",
  "email": "john@example.com",
  "source": "Google Ads",
  "service_name": "Web Development",
  "business_category": "Startup",
  "address": "Mumbai, India",
  "notes": "Interested in website redesign",
  "budget": "50000",
  "page_url": "https://mysite.com/contact"
}

Response (201):
{
  "success": true,
  "message": "Lead created successfully",
  "lead_id": "abc123xyz"
}`}</pre>
            </div>

            {/* Tips */}
            <div className="border-t border-gray-100 pt-3">
              <h3 className="font-semibold text-sm text-gray-900 mb-1">Tips</h3>
              <ul className="list-disc pl-4 space-y-1 text-gray-600 text-[11px]">
                <li>Always use <span className="font-mono bg-gray-100 px-1 rounded">POST</span> method (GET won't work)</li>
                <li>Set <span className="font-mono bg-gray-100 px-1 rounded">Content-Type: application/json</span> header</li>
                <li>The API key in the URL identifies your company — keep it secret</li>
                <li>You can regenerate the API key anytime (old key stops working immediately)</li>
                <li>Duplicate leads (same phone/email) are still created as separate entries</li>
                <li>Works with Zapier, Make.com, Google Forms, Typeform, custom websites, and any tool that can send HTTP requests</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </MobileLayout>
  );
}
